import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["toggle", "panel", "copy"];
  static values = {
    url: String,
    nativeUrl: String,
    title: String,
    copiedText: String,
  };

  connect() {
    // Native share is a touch/mobile affordance. On desktop the API may exist
    // (e.g. Chromium on Linux) but silently reject, leaving the button dead —
    // so gate on a coarse (touch) primary pointer and always open the panel on
    // desktop.
    const coarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    this.hasNativeShare =
      coarsePointer &&
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function";
    this.outsideClickHandler = this.handleOutsideClick.bind(this);
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler);
  }

  toggle(event) {
    if (this.hasNativeShare) {
      event.preventDefault();
      this.nativeShare();
      return;
    }
    const isOpen = this.element.classList.toggle("is-open");
    this.toggleTarget.setAttribute("aria-expanded", isOpen ? "true" : "false");
    if (isOpen) {
      this.panelTarget.removeAttribute("hidden");
      document.addEventListener("click", this.outsideClickHandler);
    } else {
      this.panelTarget.setAttribute("hidden", "");
      document.removeEventListener("click", this.outsideClickHandler);
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.element.classList.remove("is-open");
      this.toggleTarget.setAttribute("aria-expanded", "false");
      this.panelTarget.setAttribute("hidden", "");
      document.removeEventListener("click", this.outsideClickHandler);
    }
  }

  async nativeShare() {
    try {
      await navigator.share({
        title: this.titleValue,
        url: this.nativeUrlValue,
      });
    } catch (_err) {
      // user cancelled or share unsupported on this content
    }
  }

  openPopup(event) {
    event.preventDefault();
    const href = event.currentTarget.getAttribute("href");
    const width = 600;
    const height = 500;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      href,
      "share-popup",
      `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`,
    );
  }

  async copyLink() {
    const ok = await this.writeToClipboard(this.urlValue);
    if (ok) {
      this.flashCopied();
    }
  }

  async writeToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_err) {
        // fall through to legacy path
      }
    }
    return this.legacyCopy(text);
  }

  legacyCopy(text) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    let success = false;
    try {
      success = document.execCommand("copy");
    } catch (_err) {
      success = false;
    }
    document.body.removeChild(textarea);
    return success;
  }

  flashCopied() {
    const btn = this.copyTarget;
    const original = btn.getAttribute("aria-label");
    btn.classList.add("is-copied");
    btn.setAttribute("aria-label", this.copiedTextValue);
    clearTimeout(this._copyTimer);
    this._copyTimer = setTimeout(() => {
      btn.classList.remove("is-copied");
      btn.setAttribute("aria-label", original);
    }, 1500);
  }
}
